import { Delete, Edit, Home, Star } from '@mui/icons-material';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { Box, Chip, ListItemText, MenuItem, Select } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { LayoutSkeleton } from '../layout-skeleton';
import type { ViewSelect__Show } from './view-select.generated';

/* GraphQL */ `#graphql
fragment ViewSelect__Show on Show {
  defaultView {
    id
  }
  homeView {
    id
  }
  views {
    id
    name
    thumbnailUri
    layout {
      name
      ...LayoutSkeleton__Layout
    }
    canDestroy {
      ...AuthorizationResult
    }
  }
}
`;

export interface ViewSelectProps {
  disabled?: boolean;
  fullWidth?: boolean;
  /** The id of the element that names the select. */
  labelId?: string;
  onChange: (viewId: number | undefined) => void;
  onEdit?: (viewId: number) => void;
  onDelete?: (viewId: number) => void;
  show: ViewSelect__Show | null | undefined;
  showActions?: boolean;
  showThumbnail?: boolean;
  sx?: SxProps<Theme>;
  value: number | undefined;
}

export const ViewSelect = ({
  disabled = false,
  fullWidth = false,
  labelId,
  onChange,
  onEdit,
  onDelete,
  show,
  showActions = false,
  showThumbnail = false,
  sx,
  value,
}: ViewSelectProps) => {
  const { t } = useTranslation();
  const views = show?.views ?? [];

  // If the show changes, reset value to new show's default view ID
  // The ideal value for `onChange` is a `useState` setter, since that's stable
  // If `onChange` isn't stable there will be needless re-renders
  useEffect(() => {
    // No show, do nothing
    if (show == null) return onChange(undefined);
    // If current value is a view ID in current show, do nothing
    if (show.views.find(({ id }) => id === value)) return;
    // Otherwise, trigger onChange for new show's default view ID
    onChange(show.defaultView.id);
  }, [onChange, show, value]);

  const handleEdit = (event: React.MouseEvent, viewId: number) => {
    event.stopPropagation();
    onEdit?.(viewId);
  };

  const handleDelete = (event: React.MouseEvent, viewId: number) => {
    event.stopPropagation();
    onDelete?.(viewId);
  };

  return (
    <Select
      labelId={labelId}
      value={value ?? ''} // value is the selected view id
      onChange={(e: SelectChangeEvent<number | string>) => {
        const v = typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value;
        onChange(Number(v));
      }}
      fullWidth={fullWidth}
      disabled={disabled || show == null || value == null || views.length < 2}
      sx={{
        ...(!fullWidth && { minWidth: 400 }),
        ...sx,
        '.MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1, padding: 1 },
      }}
      renderValue={(val) => {
        const v = views.find((v) => v.id === val);
        if (!v) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            {showThumbnail &&
              (v.thumbnailUri ? (
                <img
                  src={v.thumbnailUri}
                  alt={v.name}
                  style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Tooltip title={v.layout.name}>
                  <Box sx={{ width: 80, height: 45, borderRadius: 1, overflow: 'hidden' }}>
                    <LayoutSkeleton layout={v.layout} />
                  </Box>
                </Tooltip>
              ))}
            <ListItemText
              primary={v.name}
              primaryTypographyProps={{ noWrap: true }}
              sx={{ '.MuiListItemText-primary': { fontSize: '1.2rem' } }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
              {v.id === show?.defaultView.id && (
                <Chip
                  icon={<Star />}
                  label={t('components.view_select.actions.default_chip')}
                  color="primary"
                  size="small"
                />
              )}
              {v.id === show?.homeView?.id && (
                <Chip
                  icon={<Home />}
                  label={t('components.view_select.actions.home_chip')}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>
        );
      }}
    >
      {views.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexGrow: 1,
                minWidth: 0,
                paddingY: 0.5,
              }}
            >
              {showThumbnail &&
                (option.thumbnailUri ? (
                  <img
                    src={option.thumbnailUri}
                    alt={option.name}
                    style={{
                      width: 80,
                      height: 45,
                      objectFit: 'cover',
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Tooltip title={option.layout.name}>
                    <Box
                      sx={{
                        width: 80,
                        height: 45,
                        borderRadius: 1,
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      <LayoutSkeleton layout={option.layout} />
                    </Box>
                  </Tooltip>
                ))}
              <ListItemText primary={option.name} primaryTypographyProps={{ noWrap: true }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {option.id === show?.defaultView.id && (
                <Chip
                  icon={<Star />}
                  label={t('components.view_select.actions.default_chip')}
                  color="primary"
                  size="small"
                />
              )}
              {option.id === show?.homeView?.id && (
                <Chip
                  icon={<Home />}
                  label={t('components.view_select.actions.home_chip')}
                  color="success"
                  size="small"
                />
              )}
              {showActions && onEdit && (
                <IconButton
                  label={t('components.view_select.actions.edit_view_tooltip')}
                  size="small"
                  onMouseDown={(e) => e.preventDefault()} // keep menu open
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(e, option.id);
                  }}
                  sx={{ ml: 0.5 }}
                >
                  <Edit fontSize="small" sx={{ color: 'primary.dark' }} />
                </IconButton>
              )}
              {showActions && onDelete && (
                <IconButton
                  label={t('components.view_select.actions.delete_view_tooltip')}
                  description={option.canDestroy.reasons?.fullMessages.join(', ')}
                  size="small"
                  onMouseDown={(e) => e.preventDefault()} // keep menu open
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(e, option.id);
                  }}
                  disabled={!option.canDestroy.value}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};
